import { create } from 'zustand'

import { emailApi } from '@quotech/tenant-shared/email/api'
import {
  coerceEmailBackground,
  type EmailSurfaceBackground,
} from '../lib/emailSurfaceBackground'

/**
 * Workspace layout preferences — let the operator reshape the Email
 * Hub to match how they actually work (the "customisability in
 * workflow use" request, 2026-05-30):
 *
 *   - navPosition: where the Email Hub's own nav rail lives. `bottom`
 *     (default, operator 2026-06-03) docks a compact horizontal nav
 *     under the content so the inbox + reader get the full height;
 *     `left` and `right` keep the rich vertical rail and just flip
 *     which edge it hugs.
 *   - inboxSide: which side the message LIST sits on relative to the
 *     reader pane (`right` default, operator 2026-06-03; `left` to
 *     mirror).
 *   - listWidthPx: width of the list column when a reader is open —
 *     drag the splitter to resize; persisted so it sticks.
 *   - readerExpanded: hide the list entirely and let the reader/drawer
 *     claim the full width (a focused "expand the drawer" mode).
 *   - background: Kanban-style workspace tint (theme preset / colour /
 *     optional image). Per-user chrome only — never paints HTML bodies.
 *
 * Persistence is TWO-TIER ("in memory AND database", operator
 * 2026-06-03):
 *   1. localStorage  — instant, offline, gates first paint. The store
 *      hydrates from it synchronously at module load so the chosen
 *      shape is on screen with zero network wait.
 *   2. the backend `EmailViewPreferences.layout_json` (per-user,
 *      per-org) via GET/PATCH /api/email-view-preferences/ — the
 *      DURABLE source of truth so the layout follows the operator
 *      across devices/browsers and survives a cache clear. We hydrate
 *      from it once on hub mount (server wins unless the operator has
 *      already nudged the layout this session) and debounce-save every
 *      change back to it. localStorage sits in front as the cache.
 */
export type EmailNavPosition = 'left' | 'right' | 'bottom'
export type EmailInboxSide = 'left' | 'right'

const LAYOUT_LS_KEY = 'quotech_email_layout_v1'
const LIST_WIDTH_MIN = 280
const LIST_WIDTH_MAX = 620
const LIST_WIDTH_DEFAULT = 380

interface PersistedLayout {
  navPosition: EmailNavPosition
  inboxSide: EmailInboxSide
  listWidthPx: number
  readerExpanded: boolean
  /** Soft workspace background — same keys as Kanban config_json appearance. */
  background: EmailSurfaceBackground
}

// Default workspace shape for operators who have NEVER set a layout
// (no localStorage cache + no server `layout_json`). Operator request
// 2026-06-03: the folder nav docks along the BOTTOM and the message
// LIST sits on the RIGHT out of the box. Operators who deliberately
// picked a different shape keep it — see `coerceLayout` below, which
// preserves an explicit `left` so this default change can't flip them.
const LAYOUT_DEFAULTS: PersistedLayout = {
  navPosition: 'bottom',
  inboxSide: 'right',
  listWidthPx: LIST_WIDTH_DEFAULT,
  readerExpanded: false,
  background: {},
}

function clampListWidth(px: number): number {
  if (!Number.isFinite(px)) return LIST_WIDTH_DEFAULT
  return Math.min(LIST_WIDTH_MAX, Math.max(LIST_WIDTH_MIN, Math.round(px)))
}

/** Coerce an untrusted partial layout (from localStorage OR the server
 *  `layout_json` blob) into a fully-valid PersistedLayout, falling back
 *  to defaults for any missing/garbage field. Shared so the two sources
 *  validate identically. */
function coerceLayout(parsed: Partial<PersistedLayout> | null | undefined): PersistedLayout {
  const p = parsed ?? {}
  // `left` is an explicit, valid choice — preserve it. If we treated
  // `left` as "unset" and fell back to LAYOUT_DEFAULTS, changing the
  // default to `bottom`/`right` would silently flip every operator who
  // had deliberately chosen the left rail. Only a missing/garbage value
  // falls back to the default.
  return {
    navPosition:
      p.navPosition === 'left' || p.navPosition === 'right' || p.navPosition === 'bottom'
        ? p.navPosition
        : LAYOUT_DEFAULTS.navPosition,
    inboxSide:
      p.inboxSide === 'left' || p.inboxSide === 'right'
        ? p.inboxSide
        : LAYOUT_DEFAULTS.inboxSide,
    listWidthPx: clampListWidth(p.listWidthPx ?? LIST_WIDTH_DEFAULT),
    readerExpanded: p.readerExpanded === true,
    background: coerceEmailBackground(
      (p as Partial<PersistedLayout>).background,
    ),
  }
}

function loadLayout(): PersistedLayout {
  if (typeof window === 'undefined') return { ...LAYOUT_DEFAULTS }
  try {
    const raw = window.localStorage.getItem(LAYOUT_LS_KEY)
    if (!raw) return { ...LAYOUT_DEFAULTS }
    return coerceLayout(JSON.parse(raw) as Partial<PersistedLayout>)
  } catch {
    return { ...LAYOUT_DEFAULTS }
  }
}

function persistLayout(layout: PersistedLayout): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LAYOUT_LS_KEY, JSON.stringify(layout))
  } catch {
    /* private mode / quota — fail soft, prefs just won't persist */
  }
}

// ─── Server (DB) persistence: the durable, cross-device tier ──────────
//
// localStorage above is the instant cache; the backend
// `EmailViewPreferences.layout_json` is the source of truth. We
// debounce writes (the splitter drag fires setListWidthPx on every
// mouse move — we only want ONE PATCH when it settles) and swallow all
// errors: if the BE field isn't deployed yet, the user is offline, or
// the request fails, localStorage still holds the layout, so the
// feature degrades to exactly the old behaviour.

const SERVER_SAVE_DEBOUNCE_MS = 800
let _serverSaveTimer: ReturnType<typeof setTimeout> | null = null
// Set the instant the operator nudges the layout this session. If they
// do so BEFORE the one-shot server hydrate resolves, we must NOT let the
// (older) server value clobber their fresh choice — their change is
// already being debounce-saved up to the server anyway.
let _layoutTouchedThisSession = false

async function fetchServerLayout(): Promise<PersistedLayout | null> {
  try {
    const data = await emailApi<{ layout_json?: Partial<PersistedLayout> }>(
      '/api/email-view-preferences/',
      { method: 'GET' },
    )
    const raw = data?.layout_json
    if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) {
      return coerceLayout(raw)
    }
    return null
  } catch {
    return null
  }
}

function scheduleServerSave(layout: PersistedLayout): void {
  if (typeof window === 'undefined') return
  _layoutTouchedThisSession = true
  if (_serverSaveTimer) clearTimeout(_serverSaveTimer)
  _serverSaveTimer = setTimeout(() => {
    _serverSaveTimer = null
    void emailApi('/api/email-view-preferences/', {
      method: 'PATCH',
      body: JSON.stringify({ layout_json: layout }),
    }).catch(() => {
      /* offline / BE field not deployed yet — localStorage still holds
         the layout, so this is a no-op degrade, not a failure. */
    })
  }, SERVER_SAVE_DEBOUNCE_MS)
}

/**
 * Email-hub UI state store.
 *
 * History note: an earlier copy of this file (lifted from SAM)
 * also held the OUTER app-shell sidebar state — `sidebarCollapsed`,
 * `toggleSidebar`, `setSidebarCollapsed`. That belongs to the host
 * app, not the email feature: SAM's app shell renders /jobs, /kanban,
 * /pricelist, etc., and only SAM should mutate that state. The
 * standalone hub at email.quotech.ai has no outer shell at all.
 *
 * Outer-shell collapse on entering Email is now the host's job —
 * see the optional `shellSidebarController` prop on `EmailLayout`.
 * apps/email passes nothing (no shell to collapse); a future SAM
 * migration will wire it to SAM's own uiStore.
 */
interface UiState {
  /** Inner email feature sidebar (Inbox/Starred/Sent…). Owned by
   * the email module. */
  emailSidebarCollapsed: boolean
  toggleEmailSidebar: () => void
  /**
   * True when a narrow-viewport email page is showing exclusive
   * content (opened email / compose) instead of the folder list.
   * EmailLayout reads this to hide its secondary "Email Hub"
   * hamburger bar — the shell Header already provides back +
   * section context, and stacking both chrome rows was the
   * "doubled up" symptom reported 2026-05-21.
   */
  emailMobileExclusiveView: boolean
  setEmailMobileExclusiveView: (active: boolean) => void
  /** Settings modal — opened by Cmd/Ctrl+, and the gear icon. */
  settingsOpen: boolean
  settingsTab: string | null
  openSettings: (tab?: string) => void
  closeSettings: () => void

  /** Workspace layout prefs (persisted — see PersistedLayout above). */
  navPosition: EmailNavPosition
  inboxSide: EmailInboxSide
  listWidthPx: number
  readerExpanded: boolean
  background: EmailSurfaceBackground
  setNavPosition: (p: EmailNavPosition) => void
  setInboxSide: (s: EmailInboxSide) => void
  setListWidthPx: (px: number) => void
  toggleReaderExpanded: () => void
  setReaderExpanded: (v: boolean) => void
  /** Replace the workspace background blob (Kanban-style appearance keys). */
  setBackground: (bg: EmailSurfaceBackground) => void
  resetLayout: () => void
  /** One-shot hydrate of the layout from the server (DB) source of
   *  truth. Call once on hub mount (after the tenant id is set). Server
   *  wins UNLESS the operator already nudged the layout this session.
   *  Idempotent + fail-safe. */
  hydrateLayoutFromServer: () => Promise<void>
}

export const EMAIL_LIST_WIDTH = {
  min: LIST_WIDTH_MIN,
  max: LIST_WIDTH_MAX,
  default: LIST_WIDTH_DEFAULT,
} as const

const _initialLayout = loadLayout()

export const useUiStore = create<UiState>((set, get) => ({
  emailSidebarCollapsed: false,
  toggleEmailSidebar: () => set((s) => ({ emailSidebarCollapsed: !s.emailSidebarCollapsed })),
  emailMobileExclusiveView: false,
  setEmailMobileExclusiveView: (active) => set({ emailMobileExclusiveView: active }),
  settingsOpen: false,
  settingsTab: null,
  openSettings: (tab) => set({ settingsOpen: true, settingsTab: tab ?? null }),
  closeSettings: () => set({ settingsOpen: false, settingsTab: null }),

  navPosition: _initialLayout.navPosition,
  inboxSide: _initialLayout.inboxSide,
  listWidthPx: _initialLayout.listWidthPx,
  readerExpanded: _initialLayout.readerExpanded,
  background: _initialLayout.background,
  // Every layout setter mirrors the new value into localStorage so the
  // operator's chosen workspace shape survives reloads and new tabs.
  setNavPosition: (p) =>
    set(() => {
      const next = { ...snapshotLayout(get()), navPosition: p }
      persistLayout(next)
      scheduleServerSave(next)
      return { navPosition: p }
    }),
  setInboxSide: (s) =>
    set(() => {
      const next = { ...snapshotLayout(get()), inboxSide: s }
      persistLayout(next)
      scheduleServerSave(next)
      return { inboxSide: s }
    }),
  setListWidthPx: (px) =>
    set(() => {
      const clamped = clampListWidth(px)
      const next = { ...snapshotLayout(get()), listWidthPx: clamped }
      persistLayout(next)
      scheduleServerSave(next)
      return { listWidthPx: clamped }
    }),
  toggleReaderExpanded: () =>
    set((s) => {
      const v = !s.readerExpanded
      const next = { ...snapshotLayout(s), readerExpanded: v }
      persistLayout(next)
      scheduleServerSave(next)
      return { readerExpanded: v }
    }),
  setReaderExpanded: (v) =>
    set(() => {
      const next = { ...snapshotLayout(get()), readerExpanded: v }
      persistLayout(next)
      scheduleServerSave(next)
      return { readerExpanded: v }
    }),
  setBackground: (bg) =>
    set(() => {
      const background = coerceEmailBackground(bg)
      const next = { ...snapshotLayout(get()), background }
      persistLayout(next)
      scheduleServerSave(next)
      return { background }
    }),
  resetLayout: () =>
    set(() => {
      const defaults = { ...LAYOUT_DEFAULTS, background: {} }
      persistLayout(defaults)
      scheduleServerSave(defaults)
      return { ...defaults }
    }),
  hydrateLayoutFromServer: async () => {
    const server = await fetchServerLayout()
    // The operator changed the layout while the GET was in flight —
    // honour their fresh choice, don't stomp it with the older server
    // snapshot (their change is already being debounce-saved upward).
    if (server && !_layoutTouchedThisSession) {
      persistLayout(server)
      set({ ...server })
    }
  },
}))

function snapshotLayout(s: UiState): PersistedLayout {
  return {
    navPosition: s.navPosition,
    inboxSide: s.inboxSide,
    listWidthPx: s.listWidthPx,
    readerExpanded: s.readerExpanded,
    background: s.background ?? {},
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === ',') {
      e.preventDefault()
      const { settingsOpen, openSettings, closeSettings } = useUiStore.getState()
      if (settingsOpen) closeSettings()
      else openSettings()
    }
  })
}
